import {
	TRUST_GRAPH_RISK_CLASSES,
	TRUST_GRAPH_RISK_LABELS,
	TRUST_GRAPH_TONE_CLASSES,
} from '@/lib/constants';
import { cn } from '@/lib/utils';
import type { DashboardOverviewNode } from '@uncain/shared';
import { CircleHelp } from 'lucide-react';
import { useId, useState } from 'react';

interface DashboardOverviewNodeProps {
	readonly node: DashboardOverviewNode;
	readonly assignRef: (element: HTMLDivElement | null) => void;
}

const RISK_CONTAINER_CLASSES: Record<DashboardOverviewNode['risk'], string> = {
	none: 'border-border/70 bg-card/95',
	low: 'border-severity-low/30 bg-card/95 shadow-[0_0_0_1px_rgba(74,222,128,0.08)]',
	medium: 'border-severity-medium/35 bg-card/95 shadow-[0_0_0_1px_rgba(245,158,11,0.08)]',
	high: 'border-severity-high/35 bg-card/95 shadow-[0_0_0_1px_rgba(249,115,22,0.1)]',
	critical: 'border-severity-critical/40 bg-card/95 shadow-[0_0_0_1px_rgba(239,68,68,0.12)]',
};

export function DashboardOverviewNodeCard({ node, assignRef }: DashboardOverviewNodeProps) {
	const [showHelp, setShowHelp] = useState(false);
	const tooltipId = useId();

	return (
		<div
			ref={assignRef}
			className={cn(
				'relative rounded-[1.4rem] border p-3.5 backdrop-blur-sm transition-colors',
				RISK_CONTAINER_CLASSES[node.risk],
			)}
		>
			<div
				className={cn(
					'absolute left-4 top-4 h-2.5 w-2.5 rounded-full',
					node.risk === 'none' ? 'bg-muted-foreground/30' : 'bg-current',
					TRUST_GRAPH_RISK_CLASSES[node.risk],
				)}
			/>
			<div className="pl-5">
				<div className="flex items-start justify-between gap-3">
					<div className="space-y-1">
						<div className="flex items-center gap-1.5">
							<h3 className="text-sm font-semibold text-foreground">{node.label}</h3>
							{node.subtitle && (
								<div className="relative">
									<button
										type="button"
										aria-label={`${node.label} details`}
										aria-describedby={showHelp ? tooltipId : undefined}
										onMouseEnter={() => setShowHelp(true)}
										onMouseLeave={() => setShowHelp(false)}
										onFocus={() => setShowHelp(true)}
										onBlur={() => setShowHelp(false)}
										className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
									>
										<CircleHelp className="h-3.5 w-3.5" />
									</button>
									{showHelp && (
										<div
											id={tooltipId}
											role="tooltip"
											className="absolute left-1/2 top-7 z-20 w-52 -translate-x-1/2 rounded-xl border border-border/70 bg-popover px-3 py-2 text-left text-xs leading-5 text-popover-foreground shadow-lg"
										>
											{node.subtitle}
										</div>
									)}
								</div>
							)}
						</div>
					</div>
					<span className={cn('text-xs font-medium', TRUST_GRAPH_RISK_CLASSES[node.risk])}>
						{TRUST_GRAPH_RISK_LABELS[node.risk]}
					</span>
				</div>

				<div className="mt-3 grid grid-cols-2 gap-2">
					<div className="rounded-2xl bg-muted/35 px-2.5 py-2">
						<div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
							Items
						</div>
						<div className="mt-1 text-lg font-semibold text-foreground">{node.itemCount}</div>
					</div>
					<div className="rounded-2xl bg-muted/35 px-2.5 py-2">
						<div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
							Findings
						</div>
						<div className="mt-1 text-lg font-semibold text-foreground">{node.findingCount}</div>
					</div>
				</div>

				{node.badges.length > 0 && (
					<div className="mt-3 flex flex-wrap gap-2">
						{node.badges.map((badge) => (
							<span
								key={`${node.id}-${badge.label}`}
								className={cn(
									'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium',
									TRUST_GRAPH_TONE_CLASSES[badge.tone],
								)}
							>
								<span>{badge.label}</span>
								<span>{badge.value}</span>
							</span>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
